"use client";

import { useState } from "react";
import type { SolutionAITeamTab } from "@/data/solutionsData";
import Image from "next/image";
import bgAITeamImage from "@/assets/images/solutions/team-bg.svg";
import { ChevronRightIcon } from "lucide-react";

interface SolutionsAITeamProps {
  title: string;
  titleHighlight: string;
  subtitle: string;
  tabs: SolutionAITeamTab[];
}

export default function SolutionsAITeam({
  title,
  titleHighlight,
  subtitle,
  tabs,
}: SolutionsAITeamProps) {
  const [activeId, setActiveId] = useState(tabs[0]?.id ?? "");
  const activeTab = tabs.find((t) => t.id === activeId) ?? tabs[0];

  if (!tabs.length) return null;

  return (
    <section className="relative z-10 overflow-hidden" style={{
      background: `url(${bgAITeamImage.src})`,
      backgroundColor: "#ffffff",
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "center",
    }}>
      <div className="container mx-auto px-4 py-16 xl:py-24">
        <div className="mb-10 text-center">
          <h2 className="text-2xl font-semibold text-white lg:text-3xl xl:text-4xl">
            {title}
            <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
              {titleHighlight}
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-[800px] text-base text-white/90">
            {subtitle}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 p-4 rounded-xl items-center  bg-black/30">
          <div className="flex flex-col gap-1 rounded-xl p-2 lg:col-span-4">
            {tabs.map((tab) => {
              const isActive = tab.id === activeId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveId(tab.id)}
                  className={`flex w-full items-center gap-3 rounded-lg px-4 py-6 text-left text-lg font-medium transition-colors border ${isActive
                    ? "bg-black/15 text-white border-white/10"
                    : "text-white/80 hover:bg-black/15 hover:text-white border-transparent"
                    }`}
                >
                  {tab.label}
                  <ChevronRightIcon className={`${isActive ? "rotate-90" : ""} w-4 h-4 ml-auto`} />
                </button>
              );
            })}
          </div>
          <div className="lg:col-span-8 relative aspect-video overflow-hidden">
            <Image src={activeTab?.image} alt={activeTab?.title} fill className="object-contain rounded-2xl" />
          </div>
        </div>
      </div>
    </section >
  );
}
